import React from 'react';
import './App.css';
import { Link } from 'react-scroll';
import Navbar from './navbar/Navbar';
import Footer from './footer/Footer';

import { personalInfo, skills, projects, experience, education, certifications } from './data/resume-data';

function App() {
  return (
    <div className="App">
      <Navbar />

      {/* ==============================
          HERO / ABOUT
      ============================== */}
      <div className="hero-wrapper" id="AboutMe">
        <div className="hero">
          <div className="hero-content">

            {/* Text */}
            <div className="hero-text">
              <p className="hero-eyebrow">{personalInfo.title}</p>
              <h1 className="hero-name">
                {personalInfo.name.split(' ')[0]}
                <br />
                <span className="name-accent">{personalInfo.name.split(' ')[1]}</span>
              </h1>
              <p className="hero-bio">{personalInfo.about}</p>
              <div className="hero-cta">
                <Link to="Skills" smooth={true} offset={-70} className="btn-primary">
                  View My Work
                </Link>
                <Link to="Education" smooth={true} offset={-70} className="btn-secondary">
                  My Story →
                </Link>
              </div>
            </div>

            {/* Photo */}
            <div className="hero-image-wrapper">
              <img
                src={personalInfo.photo}
                alt={personalInfo.name}
                className="hero-image"
              />
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="hero-scroll">
            <span>Scroll</span>
            <div className="scroll-line" />
          </div>
        </div>
      </div>

      {/* ==============================
          SKILLS
      ============================== */}
      <section className="Skills" id="Skills">
        <div className="section-content">
          <div className="section-tag">
            <span className="section-num">01 —</span>
            <h2>Technical Proficiency</h2>
            <div className="section-line" />
          </div>

          <div className="skills-grid">
            {skills.map((categoryGroup, index) => (
              <div
                key={index}
                className="skill-category-card"
                style={{ borderTop: `3px solid ${categoryGroup.color}` }}
              >
                <h3 style={{ color: categoryGroup.color }}>{categoryGroup.category}</h3>
                <div className="skill-items-container">
                  {categoryGroup.items.map((skill, i) => (
                    <div key={i} className="skill-detail-item">
                      <span className="skill-name">{skill.name}</span>
                      <span className="skill-details">{skill.details}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==============================
          PROJECTS
      ============================== */}
      <section className="Projects" id="Projects">
        <div className="section-content">
          <div className="section-tag">
            <span className="section-num">02 —</span>
            <h2>Projects</h2>
            <div className="section-line" />
          </div>

          <div className="project-cards-container">
            {projects.map((project, index) => {
              const isClickable = !!project.link;
              const Tag = isClickable ? 'a' : 'div';
              const tagProps = isClickable
                ? { href: project.link, target: '_blank', rel: 'noopener noreferrer' }
                : {};

              return (
                <Tag
                  key={index}
                  className={`project-card ${isClickable ? 'clickable' : 'non-clickable'}`}
                  {...tagProps}
                >
                  <div>
                    <div className="project-header">
                      <h3>{project.title}</h3>
                      {project.status && (
                        <span className="status-badge">⚙ {project.status}</span>
                      )}
                    </div>
                    <p>{project.description}</p>
                  </div>
                  <div className="tech-stack-container">
                    {project.tech.map((tech, i) => (
                      <span key={i} className="tech-pill">{tech}</span>
                    ))}
                  </div>
                </Tag>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==============================
          EXPERIENCE
      ============================== */}
      <section className="Experience" id="Experience">
        <div className="section-content">
          <div className="section-tag">
            <span className="section-num">03 —</span>
            <h2>Experience</h2>
            <div className="section-line" />
          </div>

          <div className="timeline">
            {experience.map((job, index) => (
              <div key={index} className="experience-item">
                <div className="job-header">
                  <h3>{job.role}</h3>
                  <span className="company">{job.company}</span>
                  <span className="date">{job.period}</span>
                </div>
                <ul>
                  {job.tasks.map((task, i) => (
                    <li key={i}>{task}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==============================
          EDUCATION
      ============================== */}
      <section className="Education" id="Education">
        <div className="section-content">
          <div className="section-tag">
            <span className="section-num">04 —</span>
            <h2>Education</h2>
            <div className="section-line" />
          </div>

          {education.map((edu, index) => {
            // --- GRADUATION STATUS LOGIC (unchanged) ---
            let graduationStatus = null;

            if (edu.graduationDate) {
              const today   = new Date();
              const gradDate = new Date(edu.graduationDate);
              const dateString = gradDate.toLocaleDateString('en-US', {
                month: 'long',
                year:  'numeric',
              });

              if (today >= gradDate) {
                graduationStatus = (
                  <span className="status graduated">✓ Graduated: {dateString}</span>
                );
              } else {
                graduationStatus = (
                  <span className="status expected">◌ Expected: {dateString}</span>
                );
              }
            }
            // --- END LOGIC ---

            return (
              <div key={index} className="education-item">
                <div className="edu-header">
                  <h3>{edu.degree} — NQF {edu.nqf}</h3>
                  <span className="period">{edu.period}</span>
                </div>

                <p className="institution"><strong>{edu.institution}</strong></p>

                {graduationStatus && (
                  <div className="grad-status-container">{graduationStatus}</div>
                )}

                {edu.average && (
                  <p className="edu-average">
                    Academic Average: <span className="score">{edu.average}</span>
                  </p>
                )}

                {edu.majors && (
                  <p className="edu-majors">
                    <strong>Majors:</strong> {edu.majors}
                  </p>
                )}

                {edu.details && edu.details.length > 0 && (
                  <ul>
                    {edu.details.map((detail, i) => (
                      <li key={i}>{detail}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ==============================
          CERTIFICATIONS
      ============================== */}
      <section className="Certifications" id="Certifications">
        <div className="section-content">
          <div className="section-tag">
            <span className="section-num">05 —</span>
            <h2>Certifications</h2>
            <div className="section-line" />
          </div>

          <div className="cert-grid">
            {certifications.map((cert, index) => (
              <div
                key={index}
                className="cert-card"
                style={{ borderTop: `3px solid ${cert.color}` }}
              >
                <div className="cert-card-top">
                  <div>
                    <p className="cert-issuer">{cert.issuer}</p>
                    <p className="cert-title">{cert.title}</p>
                  </div>
                  {cert.code && <span className="cert-code">{cert.code}</span>}
                </div>

                <p className="cert-description">{cert.description}</p>

                <div className="cert-footer">
                  <span className="cert-issued">Issued {cert.issued}</span>
                  <a
                    href={cert.credentialUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cert-link"
                    style={{ color: cert.color }}
                  >
                    Verify ↗
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default App;